/**
 * Centralized accessor for the user's browser locale.
 *
 * Single source of truth for any code that needs a BCP 47 locale tag for
 * Intl.* APIs, Luxon, MUI date pickers, or `Date.prototype.toLocaleString`.
 * Anything that needs to format dates, times, or numbers should use this
 * (or one of the wrappers in `formatting.ts` / `date.ts` / `time.ts` that
 * already calls it) rather than reading `navigator.language` directly.
 *
 * Resolution order:
 * 1. `navigator.language` — primary; what the user has configured in the browser.
 * 2. `Intl.DateTimeFormat().resolvedOptions().locale` — fallback when navigator
 *    is unavailable (e.g. exotic test environments).
 * 3. `'en-US'` — last-ditch default.
 */
export const getUserLocale = (): string => {
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale
  } catch {
    return 'en-US'
  }
}

/**
 * Format a Date for display in the user's locale. Convenience wrapper around
 * `Date.prototype.toLocaleDateString` that always uses {@link getUserLocale}.
 */
export const formatDate = (date: Date, options?: Intl.DateTimeFormatOptions): string =>
  date.toLocaleDateString(getUserLocale(), options)

/**
 * Format a number for display in the user's locale. Convenience wrapper around
 * `Number.prototype.toLocaleString` that always uses {@link getUserLocale}.
 *
 * For decimals control or percent/currency, prefer `formatNumber` /
 * `formatPercent` / `formatCurrency` from `./formatting`.
 */
export const formatNumberLocale = (value: number, options?: Intl.NumberFormatOptions): string =>
  value.toLocaleString(getUserLocale(), options)
